/**
 * Serializes a single CSV cell value.
 * - Primitives are stringified as-is.
 * - Arrays are joined with "; ".
 * - Plain objects are JSON-encoded (so nested data never becomes "[object Object]").
 */
const serializeCell = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.map(v => serializeCell(v)).join('; ');
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

/**
 * Reporting rows can carry a per-marketplace breakdown under `bySource`, keyed by
 * source name (e.g. { partstrader: {...}, ccc: {...} }). Instead of dumping that
 * object into a single cell (which renders as "[object Object]"), expand each row
 * into an aggregate row plus one row per source, surfacing the source name in a
 * dedicated `source` column.
 */
const expandBySource = (data: any[]): any[] => {
    const hasBySource = data.some(
        row => row && typeof row === 'object' && row.bySource
            && typeof row.bySource === 'object' && Object.keys(row.bySource).length > 0
    );

    if (!hasBySource) {
        // No breakdown present: strip any empty `bySource` so it doesn't leak as a column.
        return data.map(row => {
            if (row && typeof row === 'object' && 'bySource' in row) {
                const { bySource, ...rest } = row;
                return rest;
            }
            return row;
        });
    }

    return data.flatMap(row => {
        if (!row || typeof row !== 'object') return [row];
        const { bySource, ...rest } = row;
        const aggregateRow = { source: 'All', ...rest };

        if (!bySource || typeof bySource !== 'object' || Object.keys(bySource).length === 0) {
            return [aggregateRow];
        }

        // Identity/label fields belong to the parent entity and must not be
        // overwritten by a source entry's own copies of them.
        const IDENTITY_KEYS = ['id', 'name', 'date', 'priorDate', 'domainId', 'domainName'];

        const sourceRows = Object.entries(bySource).map(([sourceName, sourceMetrics]) => {
            const metrics = sourceMetrics && typeof sourceMetrics === 'object' ? sourceMetrics : {};
            const metricRest: Record<string, any> = {};
            for (const [key, val] of Object.entries(metrics as Record<string, any>)) {
                // Skip nested breakdowns and parent-owned identity fields.
                if (key === 'bySource' || IDENTITY_KEYS.includes(key)) continue;
                metricRest[key] = val;
            }
            return { ...rest, ...metricRest, source: sourceName };
        });

        return [aggregateRow, ...sourceRows];
    });
};

/**
 * Internal identifiers (Mongo ObjectIds and the like) should never appear in
 * exported reports. Matches `id` itself and any camelCase key ending in `Id` /
 * `Ids` (e.g. orderIds, domainId, vendorId, userId).
 */
const isInternalIdKey = (key: string): boolean => /^id$|Ids?$/.test(key);

/**
 * Converts an array of objects to CSV format and triggers a download.
 * 
 * @param data Array of objects to export
 * @param filename Name of the file to download (without extension)
 * @param headers Optional mapping of object keys to column headers. If not provided, keys are used.
 */
export const exportToCsv = (data: any[], filename: string, headers?: Record<string, string>) => {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    // Expand per-marketplace breakdowns into per-source rows (see expandBySource).
    const rowsToExport = expandBySource(data);

    // Determine columns from headers map or the exported rows
    const dataKeys = Object.keys(rowsToExport[0] ?? {});
    const columns = (headers 
        ? Object.keys(headers).filter(key => dataKeys.includes(key) || headers[key]) 
        : dataKeys
    ).filter(col => !isInternalIdKey(col));

    // Create CSV header row
    const headerRow = columns.map(col => {
        const headerLabel = headers ? (headers[col] || col) : col;
        return `"${headerLabel}"`;
    }).join(',');

    // Create CSV rows
    const rows = rowsToExport.map(row => {
        return columns.map(col => {
            const serialized = serializeCell(row?.[col]);
            // Escape quotes and wrap every value in quotes.
            return `"${serialized.replace(/"/g, '""')}"`;
        }).join(',');
    });

    const csvContent = [headerRow, ...rows].join('\n');
    
    // Create blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
